/**
 * "There is more this way" — for EVERY sideways scroller, not only the ones
 * built on ui/table.
 *
 * The hint is a mask on `.table-scroll[data-more="true"]` (and the other edge
 * on `[data-before="true"]`) in index.css. A scroller gets the attributes from
 * one of two doors, both of which land here:
 *
 *  - `installScrollMore()` (App.tsx, once) watches the document and tracks
 *    every `.table-scroll`, every `[data-scroll-more]`, and every box whose
 *    classes say it may scroll sideways (tables, filter rows, tab strips) —
 *    whatever rendered it and whenever, as PropPay's scroll-hints does.
 *  - `useScrollMore()` (hooks/use-scroll-more.ts) tracks the one element it is
 *    handed, for the regions ui/table owns.
 *
 * WHAT CHANGED (K23), each a cost the first version paid on every page:
 *
 *  - ONE ResizeObserver for everything. It was one per scroller, created on
 *    attach and never disconnected — so every table the reader had ever opened
 *    in a session was still being observed, detached, an hour later.
 *  - Removed scrollers STOP being tracked: their scroll listener and their
 *    observations are dropped when they leave the document.
 *  - Only ADDED nodes are scanned. Every mutation anywhere in the document used
 *    to trigger a `querySelectorAll` over the whole page — a toast, a tooltip,
 *    a keystroke re-rendering a list all rescanned every element in the app.
 *  - `:has()` is no longer needed: an overflow box is checked for a table
 *    directly, and a table that arrives later inside an existing box (the
 *    data loaded) finds its box by walking up.
 */

const TRACK_SELECTOR = ".table-scroll, [data-scroll-more]";
/* Every box that MAY scroll sideways — PropPay's rule (lib/scroll-hints.ts):
   tables, and also filter rows, chip strips and tab strips, which the first
   version skipped because it only tracked a box holding a table (Andrew,
   2026-09-28: "the tables are not using the versions from PropPay with the
   faded sides"). Substring matches, so `max-sm:overflow-x-auto` is found too.
   The flags decide whether anything fades: a box that fits is never flagged. */
const BOX_SELECTOR = "[class*='overflow-x-auto'], [class*='overflow-auto'], [class*='overflow-x-scroll']";

interface Entry {
  update: () => void;
  /** The element whose size moves the other side of the comparison — the table inside. */
  content: Element | null;
  offSelf: () => void;
  offContent: () => void;
  /** The scroll listener (flags only — no layout reads). */
  scroll?: () => void;
}

const entries = new Map<HTMLElement, Entry>();

// ─── One ResizeObserver, many callbacks ─────────────────────────────────────

const resizeCallbacks = new Map<Element, Set<() => void>>();
let resize: ResizeObserver | null = null;

/** Observe `el`'s size through the shared observer. Returns the unobserve. */
export function observeResize(el: Element, cb: () => void): () => void {
  if (typeof ResizeObserver === "undefined") return () => {};
  resize ??= new ResizeObserver((records) => {
    for (const r of records) resizeCallbacks.get(r.target)?.forEach((fn) => fn());
  });
  let set = resizeCallbacks.get(el);
  if (!set) {
    set = new Set();
    resizeCallbacks.set(el, set);
    resize.observe(el);
  }
  set.add(cb);
  return () => {
    const s = resizeCallbacks.get(el);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) {
      resizeCallbacks.delete(el);
      resize?.unobserve(el);
    }
  };
}

// ─── Tracking one scroller ──────────────────────────────────────────────────

/**
 * A table with its first column pinned fades its LEFT edge from the pinned
 * column's right edge, not from the box's — so the names stay crisp and what
 * slides under them fades, as PropPay's unpinned tables fade at the box edge.
 * Measured on resize only (a width read on every scroll event would force
 * layout per frame) and published as `--pin-w` for index.css.
 */
function updatePinWidth(el: HTMLElement): void {
  const cell = el.querySelector<HTMLElement>(".table-sticky-first tr > :first-child");
  const w = cell ? `${cell.offsetWidth}px` : "";
  if (el.style.getPropertyValue("--pin-w") !== w) {
    if (w) el.style.setProperty("--pin-w", w);
    else el.style.removeProperty("--pin-w");
  }
}

/** Set the two flags from the box's current geometry. A whole pixel of slack: layout is not integral. */
export function updateScrollFlags(el: HTMLElement): void {
  const more = el.scrollWidth - el.clientWidth - el.scrollLeft > 1 ? "true" : "false";
  const before = el.scrollLeft > 1 ? "true" : "false";
  if (el.dataset.more !== more) el.dataset.more = more;
  if (el.dataset.before !== before) el.dataset.before = before;
}

function observeContent(el: HTMLElement, entry: Entry) {
  const content = el.firstElementChild;
  if (content === entry.content) return;
  entry.offContent();
  entry.content = content;
  entry.offContent = content ? observeResize(content, entry.update) : () => {};
  entry.update();
}

/**
 * Track one scroller. Idempotent; returns the untrack function (which a hook
 * calls on unmount). Opt out with `data-scroll-hint="off"` — for a scroller
 * whose content is a picture of something (a PDF page), where fading the edge
 * reads as the document dissolving.
 */
export function trackScrollHint(el: HTMLElement): () => void {
  if (entries.has(el)) return () => untrackScrollHint(el);
  if (el.dataset.scrollHint === "off") return () => {};
  const onScroll = () => updateScrollFlags(el);
  const update = () => {
    updatePinWidth(el);
    updateScrollFlags(el);
  };
  const entry: Entry = { update, content: null, offSelf: () => {}, offContent: () => {} };
  entries.set(el, entry);
  /* `.scroll-hint`, not `.table-scroll`: the latter also hides the scrollbar,
     which is right for a sideways strip and wrong for a pane that scrolls
     vertically and merely MAY overflow sideways. Tables keep `.table-scroll`
     from ui/table (or get it here when the box holds a raw table). */
  el.classList.add("scroll-hint");
  if (el.querySelector("table")) el.classList.add("table-scroll");
  el.addEventListener("scroll", onScroll, { passive: true });
  entry.scroll = onScroll;
  entry.offSelf = observeResize(el, update);
  observeContent(el, entry);
  update();
  return () => untrackScrollHint(el);
}

export function untrackScrollHint(el: HTMLElement): void {
  const entry = entries.get(el);
  if (!entry) return;
  entries.delete(el);
  el.removeEventListener("scroll", entry.scroll ?? entry.update);
  entry.offSelf();
  entry.offContent();
}

/** For tests and diagnostics: how many scrollers are tracked, and how many elements the shared observer watches. */
export function scrollHintStats(): { tracked: number; observed: number } {
  return { tracked: entries.size, observed: resizeCallbacks.size };
}

// ─── The document watcher ───────────────────────────────────────────────────

function isScrollBox(el: Element): el is HTMLElement {
  return el instanceof HTMLElement && el.matches(BOX_SELECTOR);
}

/** Track whatever in `root` (inclusive) is a scroller. Only this subtree is read. */
function scanAdded(root: Element) {
  if (root instanceof HTMLElement && root.matches(TRACK_SELECTOR)) trackScrollHint(root);
  if (isScrollBox(root)) trackScrollHint(root);
  root.querySelectorAll<HTMLElement>(TRACK_SELECTOR).forEach(trackScrollHint);
  root.querySelectorAll(BOX_SELECTOR).forEach((b) => {
    if (isScrollBox(b)) trackScrollHint(b);
  });
  // A table arriving inside a box that was already on the page.
  if (root.tagName === "TABLE" || root.querySelector("table")) {
    const box = root.parentElement?.closest(BOX_SELECTOR);
    if (box instanceof HTMLElement) {
      trackScrollHint(box);
      box.classList.add("table-scroll");
    }
  }
  // A new first child of a tracked scroller (the table re-rendered): observe it instead.
  const parent = root.parentElement;
  if (parent && entries.has(parent)) observeContent(parent, entries.get(parent)!);
}

/** Drop every tracked scroller that has left the document. */
function sweepRemoved() {
  entries.forEach((_, el) => {
    if (!el.isConnected) untrackScrollHint(el);
  });
}

/**
 * Read the whole page again: drop what left, track what arrived. The fallback
 * when the per-node queue was not kept (a hidden tab, or a burst too large to
 * be worth replaying node by node).
 */
function rescanAll() {
  sweepRemoved();
  scanAdded(document.body);
  // What scanAdded does for one inserted node, done for the whole page: a
  // table that arrived inside a box already tracked, and a tracked scroller
  // whose first child was replaced.
  document.body.querySelectorAll("table").forEach((table) => {
    const box = table.parentElement?.closest(BOX_SELECTOR);
    if (box instanceof HTMLElement && entries.has(box)) box.classList.add("table-scroll");
  });
  entries.forEach((entry, el) => observeContent(el, entry));
}

/** Past this many queued nodes a frame, one full rescan is cheaper than the queue (and the queue stops growing). */
const MAX_QUEUED = 2000;

let installed = false;

export function installScrollMore(): void {
  if (installed || typeof window === "undefined" || typeof ResizeObserver === "undefined" || typeof MutationObserver === "undefined") return;
  installed = true;
  scanAdded(document.body);

  // Batched per frame: however many nodes a render inserts, one pass. Sets,
  // so a node touched twice in a frame is read once.
  //
  // A hidden tab runs no animation frames, so a queue filled while hidden
  // would only grow — holding every inserted element, detached ones too, for
  // as long as the tab stays in the background (a kiosk or a pinned tab can
  // be hidden for days). So while hidden nothing is queued: the page is
  // marked for one full rescan when it is shown again.
  const added = new Set<Element>();
  const restyled = new Set<Element>();
  let removed = false;
  let rescan = false;
  let queued = false;
  const drop = () => {
    added.clear();
    restyled.clear();
    removed = false;
  };
  const schedule = () => {
    if (queued || document.hidden) return;
    queued = true;
    requestAnimationFrame(flush);
  };
  const flush = () => {
    queued = false;
    if (rescan) {
      rescan = false;
      drop();
      rescanAll();
      return;
    }
    const batch = [...added];
    const classed = [...restyled];
    added.clear();
    restyled.clear();
    if (removed) {
      removed = false;
      sweepRemoved();
    }
    for (const node of batch) if (node.isConnected) scanAdded(node);
    // A class change is checked on the element itself, never its subtree: the
    // app toggles classes on large containers all the time.
    for (const el of classed) {
      if (!(el instanceof HTMLElement) || !el.isConnected || entries.has(el)) continue;
      if (el.matches(TRACK_SELECTOR) || isScrollBox(el)) trackScrollHint(el);
    }
  };
  new MutationObserver((records) => {
    if (rescan) return; // the whole page is read on the next frame anyway
    if (document.hidden) {
      rescan = true;
      drop();
      return;
    }
    for (const r of records) {
      if (r.type === "attributes") {
        // A class that arrives later — a view switch adding `overflow-x-auto`.
        if (r.target instanceof Element) restyled.add(r.target);
        continue;
      }
      r.addedNodes.forEach((n) => {
        if (n instanceof Element) added.add(n);
      });
      if (r.removedNodes.length) removed = true;
    }
    if (added.size + restyled.size > MAX_QUEUED) {
      rescan = true;
      drop();
    }
    if (rescan || added.size || restyled.size || removed) schedule();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && (rescan || added.size || restyled.size || removed)) schedule();
  });
}
